// DonutLabelingFieldGrid.jsx — 18-field input grid with doc_type-aware active/disabled
// and ISO validation borders.
//
// Props:
//   docType    : string           — 'B' | 'I' | 'D' | 'L' | 'N' | other
//   fields     : object           — current GT values keyed by field name
//   onChange   : (newFields) => void
//   onSaveNext : () => void (optional) — called on Ctrl/Cmd+Enter (parent global listener
//                preferred; included here so the grid itself can also trigger it)

import React from "react";
import { validateField } from "../utils/labelingValidation";

const ALL_FIELDS = [
  "doc_type",
  "plate_no",
  "vin",
  "car_name",
  "year",
  "maker",
  "owner_name",
  "owner_id_no",
  "owner_address",
  "mileage_km",
  "displacement_cc",
  "vehicle_weight_kg",
  "length_mm",
  "width_mm",
  "height_mm",
  "canceled_date",
  "bid_amount_krw",
  "cbm",
];

// Fields available (enabled) per doc_type
const FIELD_AVAILABILITY = {
  B: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "owner_address",
    "mileage_km",
    "displacement_cc",
    "vehicle_weight_kg",
    "canceled_date",
  ]),
  I: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "maker",
    "owner_name",
    "owner_id_no",
    "owner_address",
    "displacement_cc",
    "vehicle_weight_kg",
  ]),
  D: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "owner_address",
  ]),
  L: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "maker",
    "owner_name",
    "owner_id_no",
    "owner_address",
    "mileage_km",
    "displacement_cc",
    "vehicle_weight_kg",
    "length_mm",
    "width_mm",
    "height_mm",
    "canceled_date",
  ]),
  N: new Set([
    "doc_type",
    "plate_no",
    "vin",
    "car_name",
    "year",
    "owner_name",
    "owner_id_no",
    "bid_amount_krw",
  ]),
};

export default function DonutLabelingFieldGrid({
  docType,
  fields,
  onChange,
  onSaveNext,
  highlightIso = true, // false → skip red border on ISO validation failure
}) {
  // Unknown doc_type → all fields disabled
  const allowed = FIELD_AVAILABILITY[docType] || new Set();

  // Use functional updater to avoid stale-closure overwriting concurrent onAdopt calls
  const handleChange = (k, v) => onChange((prev) => ({ ...prev, [k]: v }));

  const handleKeyDown = (e) => {
    if (onSaveNext && e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSaveNext();
    }
  };

  return (
    <div
      className="field-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 2fr",
        gap: 8,
        alignItems: "center",
      }}
    >
      {ALL_FIELDS.map((k) => {
        const active = allowed.has(k);
        const v = fields[k] != null ? String(fields[k]) : "";
        const valid = active ? validateField(k, v) : { ok: true };

        return (
          <React.Fragment key={k}>
            <label
              style={{
                color: active ? "#000" : "#aaa",
                fontSize: 12,
                fontFamily: "monospace",
                textAlign: "right",
                paddingRight: 4,
                userSelect: "none",
              }}
            >
              {k}
            </label>
            <input
              type="text"
              value={v}
              disabled={!active}
              onChange={(e) => handleChange(k, e.target.value)}
              onKeyDown={handleKeyDown}
              title={(highlightIso && valid.message) || ""}
              style={{
                fontSize: 12,
                padding: "3px 8px",
                border: `1px solid ${highlightIso && !valid.ok ? "#dc2626" : "#d1d5db"}`,
                borderRadius: 4,
                outline: "none",
                background: !active ? "#f5f5f5" : "#fff",
                color: !active ? "#9ca3af" : "#111827",
                fontFamily: k === "vin" ? "monospace" : "inherit",
                cursor: !active ? "not-allowed" : "text",
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}
